import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL_PRODUCT_SERVICE } from '@/api/constant';
import CommonFilter from '@/components/common/CommonFilter';
import CommonTable, { TableColumn } from '@/components/common/commonTable/CommonTable';
import ExportOption from '@/components/common/export/ExportOption';
import ImagePreview from '@/components/common/media/ImagePreview';
import ConfirmationModel from '@/components/common/models/ConfrimationModel';
import Pagination from '@/components/common/Pagination';
import StatusDropdown from '@/components/common/StatusComponent';
import Layout from '@/components/Layout/Layout';
import Card from '@/components/UI/Card';
import { useDebounce } from '@/hooks/useDebounce';
import { usePermissions } from '@/hooks/usePermissions';
import { useSelectableTable } from '@/hooks/useSelectableTable';
import {
  IBrandsResponse,
  IBrandsResponseList,
} from '@/interfaces/brandsResponse.interface';
import { ActionConfig, PageInfo } from '@/interfaces/common.interface';
import {
  filterOptions,
  GetFilterUrl,
  getStatusWarning,
  showToastNotification,
  statusOptions,
} from '@/utility/helper';
import { RoutesLink } from '@/utility/RouteLinks';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface BrandStates {
  isLoading: boolean;
  brandStatus: string;
  brands: IBrandsResponse[];
  pageInfo: PageInfo;
  selectedBrands: Set<string | number>;
  showDeleteModel: boolean;
  selectedBrandId: string | null;
  showUpdateModel: boolean;
  orderDir: string;
  statusToUpdate: string | null;
}
const Brands = () => {
  const navigate = useNavigate();
  const { canEdit, canDelete } = usePermissions();
  const [searchInput, setSearchInput] = useState('');
  const debounceValue = useDebounce(searchInput);
  const [states, setStates] = useState<BrandStates>({
    isLoading: false,
    brandStatus: '',
    brands: [],
    pageInfo: {
      total: 0,
      totalPages: 0,
      limit: 10,
      page: 1,
    },
    selectedBrands: new Set(),
    showDeleteModel: false,
    selectedBrandId: null,
    showUpdateModel: false,
    statusToUpdate: null,
    orderDir: 'newest',
  });

  const handleChangeState = (newState: Partial<typeof states>) => {
    setStates((prev) => ({
      ...prev,
      ...newState,
    }));
  };

  const fetchBrandsList = async () => {
    try {
      const filterQuery = {
        page: states.pageInfo.page,
        limit: states.pageInfo.limit,
        status: states.brandStatus,
        search: debounceValue,
        sort: states.orderDir,
      };
      const query = GetFilterUrl(filterQuery);
      handleChangeState({ isLoading: true });
      const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.brands}?${query ? `${query}` : ''}`;
      // console.log('path', PATH);
      const res = await callApi<IBrandsResponseList>(PATH, { method: 'GET' });
      handleChangeState({
        brands: res.data,
        pageInfo: {
          limit: +res.pageInfo.limit,
          page: +res.pageInfo.page,
          total: +res.pageInfo.total,
          totalPages: +res.pageInfo.totalPages,
        },
        isLoading: false,
      });
    } catch (error) {
      ThrowError(error);
      handleChangeState({ isLoading: false });
    }
  };

  const onStatusUpdate = async (id: string, status: string) => {
    try {
      handleChangeState({ isLoading: true });
      const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.brands}/${id}`;
      await callApi(PATH, { method: 'PATCH', data: { status } });
      fetchBrandsList();
      handleChangeState({ isLoading: false });
    } catch (error) {
      ThrowError(error);
      handleChangeState({ isLoading: false });
    }
  };

  const handleDeleteBrand = async (id: string) => {
    try {
      handleChangeState({ isLoading: true });
      const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.brands}/${id}`;
      await callApi(PATH, { method: 'DELETE' });
      fetchBrandsList();
      showToastNotification('success', 'Brand deleted successfully');
    } catch (error) {
      ThrowError(error);
    } finally {
      handleChangeState({ isLoading: false });
    }
  };

  const handleChangePage = (page: number) => {
    handleChangeState({
      pageInfo: {
        ...states.pageInfo,
        page,
      },
    });
  };

  const handleSizeChange = (limit: number) => {
    handleChangeState({
      pageInfo: {
        ...states.pageInfo,
        limit,
      },
    });
  };

  useEffect(() => {
    fetchBrandsList();
  }, [
    states.pageInfo.limit,
    states.pageInfo.page,
    states.brandStatus,
    debounceValue,
    states.orderDir,
  ]);

  const { selectedItems, handleSelectAll, handleSelectItem } =
    useSelectableTable(states.brands);

  const columns: TableColumn<IBrandsResponse>[] = [
    {
      key: 'name',
      header: 'Name',
      render: (item) => {
        return (
          <ImagePreview
            src={`${API_URL_PRODUCT_SERVICE}/${item?.media[0]?.url}`}
            text={item.name}
          />
        );
      },
      className: 'p-3',
    },
    {
      key: 'productCount',
      header: 'Product',
      className: 'p-3',
    },
    {
      key: 'status',
      header: 'Status',
      render: (item) => (
        <StatusDropdown
          value={item.status}
          options={statusOptions}
          onChange={(status) => {
            handleChangeState({
              showUpdateModel: true,
              selectedBrandId: item.id,
              statusToUpdate: status,
            });
          }}
          canEditVal="brand"
        />
      ),
    },
  ];

  const brandActionConfig: Record<string, ActionConfig<IBrandsResponse>> = {
    edit: {
      text: 'Edit',
      onClick: (brand) => navigate(`${RoutesLink.addBrands}/${brand.id}`),
      permission: () => canEdit('brand'),
    },
    delete: {
      text: 'Delete',
      onClick: (brand) => {
        handleChangeState({ showDeleteModel: true, selectedBrandId: brand.id });
      },
      permission: () => canDelete('brand'),
    },
  };

  const actions = useMemo(
    () =>
      Object.values(brandActionConfig)
        .filter((action) => action.permission())
        .map(({ permission, ...rest }) => rest),
    [canEdit, canDelete],
  );

  const filterOption = [
    { value: 'az', label: 'A To Z' },
    { value: 'za', label: 'Z To A' },
    { value: 'newest', label: 'Newest First' },
    { value: 'oldest', label: 'Oldest First' },
  ];

  return (
    <>
      <Layout>
        <Card>
          <div className="flex justify-between items-center gap-3">
            <CommonFilter
              filters={filterOptions}
              onChange={(filter) => {
                handleChangeState({
                  brandStatus: filter,
                  pageInfo: { ...states.pageInfo, page: 1 },
                });
              }}
              inputPlaceHolder="Search for brands..."
              filterOptions={{
                values: filterOption,
                onChange: (value) => handleChangeState({ orderDir: value }),
              }}
              onSearch={(text) => setSearchInput(text)}
            />
            {states.brands.length !== 0 && (
              <ExportOption
                APIPath={`${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.brands}/export/excel`}
                status={states.brandStatus}
              />
            )}
          </div>
          <CommonTable
            columns={columns}
            data={states.brands}
            showCheckbox={false}
            selectedItems={selectedItems}
            onSelectAll={handleSelectAll}
            onSelectItem={handleSelectItem}
            getItemId={(item) => item.id}
            emptyMessage="No brands found"
            loading={states.isLoading}
            actions={actions}
          />
          {states.pageInfo.totalPages > 1 && (
            <Pagination
              isLoading={states.isLoading}
              paginationData={states.pageInfo}
              onPageChange={(page) => handleChangePage(page)}
              onPageSizeChange={(size) => handleSizeChange(size)}
            />
          )}
        </Card>
        {states.showDeleteModel && (
          <ConfirmationModel
            show={states.showDeleteModel}
            onClose={() => {
              handleChangeState({
                showDeleteModel: false,
                selectedBrandId: null,
              });
            }}
            onConfirm={() =>
              handleDeleteBrand(states.selectedBrandId as string)
            }
            type="brand"
          />
        )}
        {states.showUpdateModel && (
          <ConfirmationModel
            modelType="update"
            show={states.showUpdateModel}
            warning={
              states.statusToUpdate === 'inactive'
                ? getStatusWarning('brand')
                : ''
            }
            onClose={() => {
              handleChangeState({
                showUpdateModel: false,
                selectedBrandId: null,
              });
            }}
            subTitle={`This action will update the status to ${states.statusToUpdate}`}
            onConfirm={() =>
              onStatusUpdate(
                states.selectedBrandId as string,
                states.statusToUpdate as string,
              )
            }
          />
        )}
      </Layout>
    </>
  );
};

export default Brands;
